import type { IFileItem } from '../models/FileItem';
import type { IIssue } from '../models/Issue';
import type { IScanResult } from '../models/ScanResult';
import { fileBaseName, values } from './ruleUtils';

export interface IFilenamePattern {
  test: (baseName: string) => boolean;
  reason: string;
}

const VERSION_WORD: RegExp = /(^|[\s_\-.])(v\d+|final|new|latest|really|updated)(?=$|[\s_\-.])/gi;

/** Starter list; will become an editable tenant setting. Checked in order, first match wins. */
export const FILENAME_PATTERNS: IFilenamePattern[] = [
  { test: n => /^document\s*\d*$/i.test(n), reason: 'Default "Document" name' },
  { test: n => /^new (microsoft \w+ )?(text )?document$/i.test(n), reason: 'Default "New Document" name' },
  { test: n => /^(img|dsc|dscn|pxl|photo|image)[_\-\s]?\d+$/i.test(n), reason: 'Camera-generated name' },
  { test: n => /^scan[_\-\s]?\d*$/i.test(n), reason: 'Scanner-generated name' },
  { test: n => /^untitled(\s*\d+)?$/i.test(n), reason: '"Untitled" name' },
  { test: n => /^copy of\s/i.test(n), reason: '"Copy of" name' },
  { test: n => /\s*\(\d+\)$/.test(n), reason: 'Copy counter like "(1)" in name' },
  { test: n => (n.match(VERSION_WORD) || []).length >= 2, reason: 'Version words like "final", "v7" or "NEW" in name' },
  { test: n => /^\d+$/.test(n), reason: 'Name is only numbers' },
  { test: n => n.length < 4, reason: 'Name is too short to describe the file' }
];

/** Files created automatically by operating systems or Office that never belong in a library. */
const SYSTEM_FILES: { test: (name: string) => boolean; source: string }[] = [
  { test: n => n === '.ds_store' || n === '.localized', source: 'macOS folder settings file' },
  { test: n => /^\._/.test(n), source: 'macOS resource-fork file' },
  { test: n => n === 'thumbs.db' || n === 'ehthumbs.db' || n === 'desktop.ini', source: 'Windows folder settings file' },
  { test: n => /^~\$/.test(n), source: 'Office lock file left behind after editing' },
  { test: n => /\.tmp$/.test(n), source: 'Temporary file' }
];

export function getSystemFileSource(file: IFileItem): string | undefined {
  const name: string = file.name.toLowerCase();
  for (const pattern of SYSTEM_FILES) {
    if (pattern.test(name)) {
      return pattern.source;
    }
  }
  return undefined;
}

export function findSystemFiles(scan: IScanResult): IIssue[] {
  const issues: IIssue[] = [];
  for (const file of values(scan.files)) {
    const source: string | undefined = getSystemFileSource(file);
    if (source) {
      issues.push({
        id: `systemFiles:${file.id}`,
        category: 'systemFiles',
        severity: 'low',
        message: `${source}: "${file.name}"`,
        fileIds: [file.id],
        folderIds: []
      });
    }
  }
  return issues;
}

export function getPoorFilenameReason(file: IFileItem): string | undefined {
  // System files are reported by their own rule; renaming them is never the fix.
  if (getSystemFileSource(file)) {
    return undefined;
  }
  const base: string = fileBaseName(file).trim();
  for (const pattern of FILENAME_PATTERNS) {
    if (pattern.test(base)) {
      return pattern.reason;
    }
  }
  return undefined;
}

export function findPoorFilenames(scan: IScanResult): IIssue[] {
  const issues: IIssue[] = [];
  for (const file of values(scan.files)) {
    const reason: string | undefined = getPoorFilenameReason(file);
    if (reason) {
      issues.push({
        id: `poorFilenames:${file.id}`,
        category: 'poorFilenames',
        severity: 'low',
        message: `${reason}: "${file.name}"`,
        fileIds: [file.id],
        folderIds: []
      });
    }
  }
  return issues;
}

export function findEmptyFiles(scan: IScanResult): IIssue[] {
  return values(scan.files)
    .filter((f: IFileItem) => f.size === 0 && !getSystemFileSource(f))
    .map(f => ({
      id: `emptyFiles:${f.id}`,
      category: 'emptyFiles' as const,
      severity: 'low' as const,
      message: `"${f.name}" is empty (0 bytes)`,
      fileIds: [f.id],
      folderIds: []
    }));
}
